/**
 * Keyboard shortcuts for the calendar arche.
 *
 * Registers global keydown listeners and suppresses them when a dialog/modal
 * is open or an input element is focused.
 */

import { useEffect, useCallback } from 'react';
import type { CalendarView } from '../types';

export interface CalendarShortcutActions {
  onNewEvent: () => void;
  onToday: () => void;
  onPrev: () => void;
  onNext: () => void;
  onViewChange: (view: CalendarView) => void;
  onOpenCommandPalette: () => void;
  onOpenShortcutHelp: () => void;
}

function isInputFocused(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName.toLowerCase();
  return (
    tag === 'input' ||
    tag === 'textarea' ||
    tag === 'select' ||
    (el as HTMLElement).isContentEditable
  );
}

function isDialogOpen(): boolean {
  // Radix Dialog/Sheet renders with [role="dialog"]
  return document.querySelector('[role="dialog"]') !== null;
}

export function useCalendarShortcuts(actions: CalendarShortcutActions) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Cmd/Ctrl+K always opens command palette regardless of context
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        actions.onOpenCommandPalette();
        return;
      }

      // Escape closes dialogs — handled by Radix, nothing extra needed here

      // All other shortcuts are suppressed when input is focused or dialog is open
      if (isInputFocused() || isDialogOpen()) return;

      switch (e.key) {
        case 'n':
          e.preventDefault();
          actions.onNewEvent();
          break;
        case 't':
          e.preventDefault();
          actions.onToday();
          break;
        case 'd':
          e.preventDefault();
          actions.onViewChange('day');
          break;
        case 'w':
          e.preventDefault();
          actions.onViewChange('week');
          break;
        case 'm':
          e.preventDefault();
          actions.onViewChange('month');
          break;
        case 'a':
          e.preventDefault();
          actions.onViewChange('agenda');
          break;
        case 'ArrowLeft':
          e.preventDefault();
          actions.onPrev();
          break;
        case 'ArrowRight':
          e.preventDefault();
          actions.onNext();
          break;
        case '/':
          e.preventDefault();
          actions.onOpenCommandPalette();
          break;
        case '?':
          e.preventDefault();
          actions.onOpenShortcutHelp();
          break;
      }
    },
    [actions],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}
